<template>
  <div class="view-page">
    <title-bar title="Control de acceso y operación" :slotData="true">
        <template slot="content-actions">
          <div class="buttons is-vcentered is-pulled-right title-actions-controls" v-if="titleAction.visible">
            <div v-if="showUsersSearch" class="users-search-control">
              <div class="users-search-control__input-wrap">
                <input
                  class="input users-search-control__input"
                  type="text"
                  placeholder="Buscar usuario..."
                  v-model="usersSearchDraft"
                  @input="onUsersSearchInput"
                >
                <button
                  v-if="usersSearchDraft"
                  class="button users-search-control__clear"
                  type="button"
                  @click="clearUsersSearch"
                  aria-label="Limpiar búsqueda"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
            <button
              class="button is-intelligential"
              :disabled="titleAction.disabled"
              @click="onTitleActionClick"
            >
              {{ titleAction.label }}
            </button>
          </div>
        </template>
      </title-bar>
      <div class="tabs tabs-v3 is-small">
            <ul slot="content-actions">
              <li v-if="hasUserAccess" :class="{ 'active-customer': $route.name === 'profile-users' }">
                <router-link :to="{ name: 'profile-users' }">
                  Usuarios
                </router-link>
              </li>
              <li v-if="hasRolesAccess" :class="{ 'active-customer': isRolesRoute }">
                <router-link :to="{ name: 'profile-roles' }">
                  Roles
                </router-link>
              </li>
              <li :class="{ 'active-customer': $route.name === 'profile-branches' }">
                <router-link :to="{ name: 'profile-branches' }">
                  Sucursales
                </router-link>
              </li>
              <li v-if="hasBrokersAccess" :class="{ 'active-customer': $route.name === 'profile-consultants' }">
                <router-link :to="{ name: 'profile-consultants' }">
                  Asesores
                </router-link>
              </li>
              <li :class="{ 'active-customer': $route.name === 'profile-configuration' }">
                <router-link :to="{ name: 'profile-configuration' }">
                  Configuraciones
                </router-link>
              </li>
              <hr />
            </ul>
          </div>
      <div class="menu-content">
        <router-view :usersSearch="usersSearch" @update-title-action="updateTitleAction"></router-view>
      </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import mixins from '@/mixins/formats'

export default {
  components: {
    TitleBar: () => import('@/components/ui/TitleBar.vue')
  },
  mixins: [mixins],
  data () {
    return {
      enableUsers: false,
      enableRoles: false,
      enableBrokers: false,
      titleAction: {
        label: '',
        visible: false,
        disabled: false,
        handler: null
      },
      usersSearch: '',
      usersSearchDraft: '',
      usersSearchDebounceId: null
    }
  },
  methods: {
    ...mapActions(['getServicePermission']),
    getDefaultTitleActionByRoute (routeName = this.$route.name) {
      switch (routeName) {
        case 'profile-users':
          return { label: 'Nuevo usuario', visible: false, disabled: false, handler: null }
        case 'profile-roles':
          return { label: 'Nuevo rol', visible: false, disabled: false, handler: null }
        case 'profile-consultants':
          return { label: 'Nuevo asesor', visible: false, disabled: false, handler: null }
        case 'profile-branches':
          return { label: 'Nueva sucursal', visible: true, disabled: true, handler: null }
        default:
          return { label: '', visible: false, disabled: false, handler: null }
      }
    },
    updateTitleAction (action = {}) {
      this.titleAction = {
        ...this.getDefaultTitleActionByRoute(),
        ...action
      }
    },
    onTitleActionClick () {
      if (this.titleAction.disabled) return
      if (typeof this.titleAction.handler === 'function') {
        this.titleAction.handler()
      }
    },
    onUsersSearchInput () {
      if (this.usersSearchDebounceId) {
        clearTimeout(this.usersSearchDebounceId)
      }
      this.usersSearchDebounceId = setTimeout(() => {
        this.usersSearch = this.usersSearchDraft || ''
      }, 300)
    },
    clearUsersSearch () {
      if (this.usersSearchDebounceId) {
        clearTimeout(this.usersSearchDebounceId)
      }
      this.usersSearchDraft = ''
      this.usersSearch = ''
    },
    async hasCustomRoleAccess () {
      const resp = await this.getServicePermission(['Roles', 'Users', 'Brokers'])
      const filter = resp.filter(service => service.enabled).map(service => service.code)
      this.enableUsers = filter.includes('Users')
      this.enableRoles = filter.includes('Roles')
      this.enableBrokers = filter.includes('Brokers')
    }
  },
  computed: {
    ...mapState({
      systemConfiguration: state => state.configuration,
      profile: (state) => state.financialCompany.financialCompany,
      role: state => state.financialCompany?.role?.code || ''
    }),
    hasUserAccess () {
      const adminRoles = ['admin', 'manager', 'compliance']
      return adminRoles.includes(this.role) || this.enableUsers
    },
    hasRolesAccess () {
      const adminRoles = ['admin', 'manager', 'compliance']
      return adminRoles.includes(this.role) || this.enableRoles
    },
    hasBrokersAccess () {
      const adminRoles = ['admin', 'manager', 'compliance']
      return adminRoles.includes(this.role) || this.enableBrokers
    },
    showUsersSearch () {
      return this.$route.name === 'profile-users'
    },
    isRolesRoute () {
      return ['profile-roles', 'profile-role-detail'].includes(this.$route.name)
    }
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler () {
        this.updateTitleAction()
      }
    },
    role (newValue) {
      if (newValue === 'custom') {
        this.hasCustomRoleAccess()
      }
    }
  },
  mounted () {
    if (this.role === 'custom') {
      this.hasCustomRoleAccess()
    }
  },
  beforeDestroy () {
    if (this.usersSearchDebounceId) {
      clearTimeout(this.usersSearchDebounceId)
    }
  }
}
</script>

<style lang="scss" scoped>
.menu-content {
  margin-top: 1rem;
}
.tabs ul {
  border-bottom: 1px solid #dbdbdb;
  align-items: flex-end;
  font-size: 11px;
}
.active-customer {
  background: var(--blue-background);
  border-radius: 5px 5px 0 0;
  color: #50372e;
  font-weight: 600;
}
.users-search-control {
  display: flex;
  align-items: center;
  margin-right: 10px;
}
.users-search-control__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.users-search-control__input {
  width: 260px;
  height: 35px;
  border-radius: 999px;
  border: 1px solid #d0dae6;
  padding: 0 36px 0 14px;
  font-size: 13px;
}
.users-search-control__clear {
  position: absolute;
  right: 4px;
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #6b6d73;
  padding: 0;
  top: 50%;
  transform: translateY(-50%);
}
.title-actions-controls {
  align-items: center !important;
  margin-bottom: 0;
}
.title-actions-controls .button {
  margin-bottom: 0;
  height: 35px;
}
</style>
